// templateExport.js
// Draws the Base Template preview onto a canvas and downloads it as a PNG. The preview itself is HTML/CSS (container-query
// units), which cannot be captured reliably, so this mirrors its layout in canvas code: every size is a fraction of the
// image width ("u" = 1% of the width, the same unit as the preview's cqw), so the download matches what is on screen.
// The store name and contact details are real text drawn here; nothing is produced by AI.

const LONG_SIDE = 2048;

/** Pixel size for an "w:h" aspect ratio, with the long side at LONG_SIDE. */
export function exportSize(aspectRatio) {
  const [w, h] = String(aspectRatio || "16:9").split(":").map(Number);
  const rw = w > 0 ? w : 16;
  const rh = h > 0 ? h : 9;
  return rw >= rh ? { width: LONG_SIDE, height: Math.round((LONG_SIDE * rh) / rw) } : { width: Math.round((LONG_SIDE * rw) / rh), height: LONG_SIDE };
}

const loadImage = (url) =>
  new Promise((resolve) => {
    if (!url) return resolve(null);
    const img = new Image();
    img.crossOrigin = "anonymous"; // keeps the canvas exportable; if the server refuses, the image is skipped
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });

const fontFor = (family, weight, px) => `${weight} ${px}px ${family ? `"${String(family).replace(/["\\]/g, "")}", ` : ""}system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`;

function fit(ctx, text, maxWidth) {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let t = text;
  while (t.length > 1 && ctx.measureText(`${t}…`).width > maxWidth) t = t.slice(0, -1);
  return `${t}…`;
}

function drawCover(ctx, img, W, H) {
  const scale = Math.max(W / img.width, H / img.height);
  const w = img.width * scale;
  const h = img.height * scale;
  ctx.drawImage(img, (W - w) / 2, (H - h) / 2, w, h);
}

/** Size of the logo in pixels for a placement: height from the size setting, capped in width, aspect ratio kept. */
function logoBox(img, position, scale, u) {
  const ar = img.width / img.height || 1;
  let h = (position === "center" ? 11 : 6) * scale * u;
  let w = h * ar;
  const maxW = Math.min(30 * scale, 90) * u;
  if (w > maxW) {
    w = maxW;
    h = w / ar;
  }
  return { w, h };
}

/** Draws the template. `images` = { logo, reference } (loaded Image elements or null). Returns nothing. */
export function drawTemplate(ctx, W, H, draft, images = {}) {
  const { brand, background, layout } = draft.config;
  const u = W / 100;
  const pad = 2.4 * u;
  const gap = 1.8 * u;
  const inner = W - 2 * pad;
  const colors = brand.colors;
  const logoScale = (layout.logoSize ?? 100) / 100;
  const nameCenter = layout.storeNamePosition === "center";
  const logoCenter = layout.logoPosition === "center";

  // Background: solid colour, or the reference image when the template keeps it.
  ctx.fillStyle = background.color || colors.background;
  ctx.fillRect(0, 0, W, H);
  if ((background.type === "image" || background.type === "ai") && images.reference) drawCover(ctx, images.reference, W, H); // reference image, or the AI-made background

  ctx.textBaseline = "middle";
  let top = pad;
  let bottom = H - pad;

  // Footer: contact details in their own row (nothing is drawn when there are none).
  const contact = [brand.contact?.phone, brand.contact?.website, brand.contact?.address].filter(Boolean);
  if (contact.length) {
    const size = 2 * u;
    const lineH = size * 1.5;
    const footerTop = bottom - (lineH + 1.2 * u);
    ctx.fillStyle = colors.accent;
    ctx.fillRect(pad, footerTop, inner, Math.max(1, u * 0.12));
    ctx.fillStyle = colors.text;
    ctx.font = fontFor(brand.fonts?.body, "400", size);
    ctx.textAlign = "center";
    ctx.fillText(fit(ctx, contact.join("   •   "), inner), W / 2, footerTop + 1.2 * u + lineH / 2);
    bottom = footerTop - gap;
  }

  // Promotional banner (only if the template already has one).
  const promo = layout.slots?.promo;
  if (promo?.enabled && promo.text) {
    const size = 2.2 * u;
    const h = size * 1.9;
    ctx.fillStyle = colors.accent;
    ctx.beginPath();
    (ctx.roundRect ? ctx.roundRect(pad, bottom - h, inner, h, 0.5 * u) : ctx.rect(pad, bottom - h, inner, h));
    ctx.fill();
    ctx.fillStyle = colors.primary;
    ctx.font = fontFor(brand.fonts?.body, "600", size);
    ctx.textAlign = "center";
    ctx.fillText(fit(ctx, promo.text, inner - 2 * u), W / 2, bottom - h / 2);
    bottom -= h + gap;
  }

  const nameText = (brand.businessName || "").trim(); // the store name is optional: nothing is drawn without one
  const nameBox = (px) => {
    ctx.font = fontFor(brand.fonts?.heading, "700", px);
    const text = fit(ctx, nameText, inner);
    return { text, w: ctx.measureText(text).width, h: px * 1.2, px };
  };
  const drawName = (box, x, y) => {
    ctx.font = fontFor(brand.fonts?.heading, "700", box.px);
    ctx.fillStyle = colors.primary;
    ctx.textAlign = "left";
    ctx.fillText(box.text, x, y);
  };

  // Header: the logo and/or store name placed at the top, centered as one row.
  const topLogo = !logoCenter && images.logo ? logoBox(images.logo, "top", logoScale, u) : null;
  const topName = !nameCenter && nameText ? nameBox(4.6 * u) : null;
  if (topLogo || topName) {
    const parts = [topLogo, topName].filter(Boolean);
    const rowH = Math.max(...parts.map((p) => p.h));
    const rowW = parts.reduce((sum, p) => sum + p.w, 0) + (parts.length - 1) * 1.6 * u;
    let x = (W - rowW) / 2;
    const mid = top + rowH / 2;
    if (topLogo) {
      ctx.drawImage(images.logo, x, mid - topLogo.h / 2, topLogo.w, topLogo.h);
      x += topLogo.w + 1.6 * u;
    }
    if (topName) drawName(topName, x, mid);
    top += rowH + gap;
  }

  // Center layer: logo and/or store name stacked in the middle of the open area, each with its own contrast.
  const centerLogo = logoCenter && images.logo ? logoBox(images.logo, "center", logoScale, u) : null;
  const centerName = nameCenter && nameText ? nameBox(14 * u) : null;
  if (centerLogo || centerName) {
    const parts = [centerLogo, centerName].filter(Boolean);
    const total = parts.reduce((sum, p) => sum + p.h, 0) + (parts.length - 1) * 1.6 * u;
    let y = top + Math.max(0, (bottom - top - total) / 2);
    if (centerLogo) {
      ctx.globalAlpha = (layout.logoContrast ?? 100) / 100;
      ctx.drawImage(images.logo, (W - centerLogo.w) / 2, y, centerLogo.w, centerLogo.h);
      ctx.globalAlpha = 1;
      y += centerLogo.h + 1.6 * u;
    }
    if (centerName) {
      ctx.globalAlpha = (layout.storeNameContrast ?? 40) / 100;
      drawName(centerName, (W - centerName.w) / 2, y + centerName.h / 2);
      ctx.globalAlpha = 1;
    }
  }
}

/** Renders the draft to a canvas. `skipped` lists images (logo / reference) that could not be loaded for the export. */
export async function renderTemplateCanvas(draft) {
  const { width, height } = exportSize(draft.config.layout.aspectRatio);
  const wantsReference = draft.config.background.type === "image" && !!draft.referenceImageUrl;
  const wantsBackground = draft.config.background.type === "ai" && !!draft.backgroundImageUrl;
  const [logo, reference] = await Promise.all([
    loadImage(draft.logoUrl),
    wantsBackground ? loadImage(draft.backgroundImageUrl) : wantsReference ? loadImage(draft.referenceImageUrl) : null,
  ]);
  const skipped = [];
  if (draft.logoUrl && !logo) skipped.push("logo");
  if (wantsReference && !reference) skipped.push("reference image");
  if (wantsBackground && !reference) skipped.push("background image");
  try {
    const fam = [draft.config.brand.fonts?.heading, draft.config.brand.fonts?.body].filter(Boolean);
    if (document.fonts?.load) await Promise.all(fam.map((f) => document.fonts.load(fontFor(f, "700", 20)).catch(() => {})));
  } catch {
    /* fonts that are not available fall back to the system font */
  }
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  drawTemplate(canvas.getContext("2d"), width, height, draft, { logo, reference });
  return { canvas, skipped };
}

const slug = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60);

/** Saves the preview as a PNG file. Resolves to { skipped } so the caller can say which images were left out. */
export async function downloadTemplatePreview(draft) {
  const { canvas, skipped } = await renderTemplateCanvas(draft);
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
  if (!blob) throw new Error("Couldn't create the image.");
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${slug(draft.name) || slug(draft.config.brand.businessName) || "base-template"}-preview.png`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return { skipped };
}
